import { ReactElement, useMemo, useRef, useState } from 'react'
import {
  DrawControllerWrapper,
  hashObject,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import { find, isNull } from 'lodash-es'
import {
  InteractiveLine,
  InteractiveLineHandles,
  InteractiveLineRef,
  TransientDrawState,
  useCanvasListener,
  ImmutableVector3,
} from '@modugen/ui-toolkit'
import { Torus } from '@react-three/drei'
import { useControlStore, useModelStore } from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { roundXYImmutableVector3 } from 'src/geometry/rounding'
import { useModelSnapTargets } from '../../hooks'
import { NUM_DECIMALS_ROUND_BEAM_EDIT } from './constants'

const interactiveLineName = 'interactive-beam-line'

interface Props {
  beamGuid: string
  onEditStart: () => void
  onEditStop: () => void
  reset: () => void
}

const BeamEdit = ({ beamGuid, onEditStart, onEditStop }: Props): ReactElement => {
  const lineRef = useRef<InteractiveLineRef>(null)

  const { beams } = useModelStore(state => state.model)
  const updateBeam = useModelStore(state => state.updateBeam)

  const beam = useMemo(() => find(beams, ['guid', beamGuid]), [beams, beamGuid]) as ShapeObjectLine

  const [activeHandle, setActiveHandle] = useState<
    typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles] | null
  >(null)

  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)

  const modelSnapTargets = useModelSnapTargets({
    xyOnly: true,
  })
  const tapelineSnapTargets = useTapelineSnapTargets(true)
  const tapelineCenterTargets = useTapelineCentersSnapTargets(true)

  const additionalSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets, ...tapelineCenterTargets],
    [modelSnapTargets, tapelineSnapTargets, tapelineCenterTargets],
  )

  // Effects
  useDefault2DSnapConfigForElementType(SnapTargetElementType.BEAMS)

  // HOTKEYS

  useStructuralPlanningDrawerEsc(() => {
    onEditStop()
    if (activeHandle) {
      setActiveHandle(null)
      lineRef.current?.reset()
    }
  }, !!activeHandle)

  // MEMOS

  const beamStart = useMemo(
    () => new ImmutableVector3(beam.shape.start.x, beam.shape.start.y, beam.shape.start.z),
    [beam.shape.start],
  )

  const beamEnd = useMemo(
    () => new ImmutableVector3(beam.shape.end.x, beam.shape.end.y, beam.shape.end.z),
    [beam.shape.end],
  )

  // EVENTS

  const editStop = () => {
    const beamLine = lineRef.current?.transientLine.current

    if (beamLine) {
      const [drawerStart, drawerEnd] = beamLine
      const start = roundXYImmutableVector3(drawerStart, NUM_DECIMALS_ROUND_BEAM_EDIT)
      const end = roundXYImmutableVector3(drawerEnd, NUM_DECIMALS_ROUND_BEAM_EDIT)

      const newBeam = {
        guid: beam.guid,
        shape: {
          start: new ImmutableVector3(start.x, start.y, beam.shape.start.z),
          end: new ImmutableVector3(end.x, end.y, beam.shape.end.z),
        },
      }
      updateBeam(newBeam)
    }
  }

  const stopEditing = () => {
    editStop()
    setActiveHandle(null)
    onEditStop()
  }

  useCanvasListener(
    'mouseup',
    event => {
      const isInput = event.target === lineRef.current?.input.current

      if (!isInput && !lineRef.current?.inputActive.current && activeHandle) {
        stopEditing()
      }
    },
    [activeHandle, editStop, stopEditing],
  )

  const extendLine = (transientDrawState: TransientDrawState) => {
    if (lineRef.current?.inputActive.current) return

    if (activeHandle && transientDrawState.drawPoint) {
      const drawPoint = transientDrawState.drawPoint

      lineRef.current?.updateHandle(activeHandle, drawPoint)
    }
  }

  const onExtendStart = (
    handle: typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles],
  ) => {
    if (isNull(activeHandle)) {
      onEditStart()
      setActiveHandle(handle)
    }
  }

  const zTranslation = 10

  const drawControllerEnabled = useClickActionEnabled()

  return (
    <>
      {/* draw controller for extending */}
      <DrawControllerWrapper
        // drawingAxis={{
        //   origin: beamCenter,
        //   direction: beamDirection,
        // }}
        indicatorType="crosshair"
        enableIndicator={false}
        enabled={!!activeHandle && drawControllerEnabled}
        onMouseMove={extendLine}
        additionalSnapTargets={additionalSnapTargets}
        snapToAngles={snapToAngles}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        xyOnly
      />

      <group position={[0, 0, zTranslation]}>
        <InteractiveLine
          key={hashObject(beam)}
          mainLineProps={{ name: interactiveLineName }}
          boundingLineProps={{ name: interactiveLineName }}
          ref={lineRef}
          line={[beamStart, beamEnd]}
          clickDisabled={!!activeHandle}
          showHandles={!beam.is_local}
          onClickHandle={onExtendStart}
          clickHandleDisabled={false}
          clickableHandles={activeHandle ? [activeHandle] : undefined}
          nonSelectable={!!activeHandle}
          cursor={beam.is_local ? 'auto' : 'grab'}
          indicatorCursor={beam.is_local ? 'auto' : 'grab'}
          handleColor={activeHandle ? 'purple' : undefined}
        />

        {/* Red torus at beam start, indicating start */}
        <Torus position={[beamStart.x, beamStart.y, 0]} args={[0.15, 0.01, 8, 16]}>
          <meshBasicMaterial color="red" />
        </Torus>
        {/* Black torus at beam end, indicating end */}
        <Torus position={[beamEnd.x, beamEnd.y, 0]} args={[0.15, 0.01, 8, 16]}>
          <meshBasicMaterial color="black" />
        </Torus>
      </group>
    </>
  )
}

export default BeamEdit
